import { Component } from '@theme/component';
import { addItems } from '@theme/holdra-cart';

/**
 * @typedef {{ id: number, size: string | null, available: boolean }} PackVariant
 *
 * @typedef {object} PackBuilderRefs
 * @property {HTMLScriptElement} data
 * @property {HTMLButtonElement} add
 * @property {HTMLElement} message
 */

/**
 * Mix-and-match pack on its product page: adds the pack for the chosen size, with the colour of each piece
 * saved on the order line (e.g. "Farger: Svart, Rosa, Vinrød").
 *
 * @extends {Component<PackBuilderRefs>}
 */
class PackBuilderComponent extends Component {
  requiredRefs = ['data', 'add', 'message'];

  async add() {
    const { add, message } = this.refs;
    if (add.getAttribute('aria-busy') === 'true') return;

    /** @type {{ variants: PackVariant[] }} */
    const data = JSON.parse(this.refs.data.textContent || '{"variants":[]}');
    const size = this.querySelector('.pack-builder__sizes input:checked');
    const variant =
      size instanceof HTMLInputElement
        ? data.variants.find((entry) => entry.size === size.value)
        : data.variants.length === 1
          ? data.variants[0]
          : undefined;

    if (!variant || !variant.available) {
      this.#show(variant ? this.dataset.soldOutLabel || '' : this.dataset.unavailableLabel || '');
      return;
    }

    const picker = this.querySelector('pack-colour-picker');
    /** @type {Record<string, string> | undefined} */
    let properties;
    if (picker && 'value' in picker && 'property' in picker && picker.value) {
      properties = { [String(picker.property)]: String(picker.value) };
    }

    this.#show('');
    add.setAttribute('aria-busy', 'true');
    const result = await addItems(this, [{ id: variant.id, quantity: 1, properties }], {
      action: 'add',
      context: 'product',
      origin: 'pack-builder',
    });
    add.removeAttribute('aria-busy');
    if (!result.ok) this.#show(result.message || this.dataset.unavailableLabel || '');
    message.toggleAttribute('hidden', !message.textContent);
  }

  /** @param {string} text */
  #show(text) {
    this.refs.message.textContent = text;
    this.refs.message.hidden = !text;
  }
}

if (!customElements.get('pack-builder-component')) {
  customElements.define('pack-builder-component', PackBuilderComponent);
}
